import axios from 'axios';
import { KeyboardEventHandler, useEffect, useState } from 'react';
import { Breadcrumb } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useOperatorsState } from '../../services/OperatorsList';
import { useAuthState } from '../../services/auth';
import Form from 'react-bootstrap/Form';
import './settings.scss';
import { WebFilters } from '../web-filters/WebFilters';
import { CvssThresholdConfig } from './CvssThresholdConfig';
import { useToast } from '../../services/ToastContext';
import { Thresholds } from '../thresholds/Thresholds';
import { UISettings } from '../ui-settings/UISettings';
import { PrivateDNS } from '../private-DNS/PrivateDNS';
import { LanguageSelector } from '../../components/language-selector/LanguageSelector';
import { ChevronDown, ChevronUp, Languages, LucideIcon, Palette, ShieldCheck, SlidersHorizontal } from 'lucide-react';
import '../../design-system/index.css';
import { useTranslation } from 'react-i18next';

const backgroundDefault = '';
const backgroundGreen = '#3BFF00';
const backgroundOrange = '#FFFC00';
const backgroundRed = '#FF0000';
const backgroundBlack = '#101820';

export const EnvironmentSettings = ({ onLanguageChange, selectedLanguage, darkmode }: any) => {
  const { t } = useTranslation();
  const { operatorId, cpeId, deviceId } = useParams();
  const [summaryData, setSummaryData] = useState<any>();
  const { operatorList, loggedUserData } = useOperatorsState();
  const [selectedMenu, setSelectedMenu] = useState('Global Filters');

  const [initialLowSwitch, setInitialLowSwitch] = useState(false);
  const [initialMediumSwitch, setInitialMediumSwitch] = useState(false);
  const [initialHighSwitch, setInitialHighSwitch] = useState(true);
  const [initialCriticalSwitch, setInitialCriticalSwitch] = useState(true);
  const [lowSwitch, setLowSwitch] = useState<boolean>(false);
  const [mediumSwitch, setMediumSwitch] = useState<boolean>(false);
  const [highSwitch, setHighSwitch] = useState<boolean>(false);
  const [criticalSwitch, setCriticalSwitch] = useState<boolean>(false);
  const [prevLowSwitch, setPrevLowSwitch] = useState(false);
  const [prevMediumSwitch, setPrevMediumSwitch] = useState(false);
  const [prevHighSwitch, setPrevHighSwitch] = useState(true);
  const [prevCriticalSwitch, setPrevCriticalSwitch] = useState(true);
  const [changesMade, setChangesMade] = useState(false);

  const { showToastMessage } = useToast();
  const [selectedLows, setSelectedLows] = useState<any>([0, 1, 2]);
  const [selectedMediums, setSelectedMediums] = useState<any>([3, 4, 5, 6]);
  const [selectedHighs, setSelectedHighs] = useState<any>([7, 8]);
  const [selectedCriticals, setSelectedCriticals] = useState<any>([9]);
  // Threshold states
  const [lowThreshold, setLowThreshold] = useState(3.9);
  const [mediumThreshold, setMediumThreshold] = useState(6.9);
  const [highThreshold, setHighThreshold] = useState(8.9);
  const [criticalThreshold, setCriticalThreshold] = useState(10.0);

  const [lowAlertsEnabled, setLowAlertsEnabled] = useState(false);
  const [mediumAlertsEnabled, setMediumAlertsEnabled] = useState(true);
  const [highAlertsEnabled, setHighAlertsEnabled] = useState(true);
  const [criticalAlertsEnabled, setCriticalAlertsEnabled] = useState(true);

  // Dragging states
  const [dragging, setDragging] = useState<string | null>(null);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartValue, setDragStartValue] = useState(0);
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);

  const menuList = ['Global Filters', 'CVSS Alerts', 'UI Settings', 'Select Language'];
  const arrayNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  const [masterOperatorId, setMasterOperatorId] = useState<any | null>();

  const tabIcons: Record<string, LucideIcon> = {
    'Global Filters': ShieldCheck,
    'CVSS Alerts': SlidersHorizontal,
    'UI Settings': Palette,
    'Select Language': Languages,
  };
  // Display labels for the tab ids above (ids stay as internal state values)
  const tabLabels: Record<string, string> = {
    'Global Filters': t('settings.tabs.globalFilters'),
    'CVSS Alerts': t('settings.tabs.cvssAlerts'),
    'UI Settings': t('settings.tabs.uiSettings'),
    'Select Language': t('settings.tabs.selectLanguage'),
  };
  const theme = darkmode ? 'dark' : 'light';

  useEffect(() => {
    const data: any = localStorage.getItem('loggedUserData');
    if (data) {
      const parsedData = JSON.parse(data);
      setMasterOperatorId(parsedData.operatorId);
    }
  }, []);

  // Dragging functionality
  const calculateValueFromPosition = (clientX: number, container: HTMLDivElement) => {
    const rect = container.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percentage = (x / rect.width) * 100;
    return (percentage / 100) * 10;
  };

  const handleDragStart = (threshold: string, clientX: number) => {
    setDragging(threshold);
    setDragStartX(clientX);
    setDragStartValue(
      threshold === 'low' ? lowThreshold :
        threshold === 'medium' ? mediumThreshold :
          threshold === 'high' ? highThreshold :
            10
    );
  };

  const handleDragMove = (clientX: number) => {
    if (!dragging || !containerRef) return;

    const deltaX = clientX - dragStartX;
    const containerWidth = containerRef.offsetWidth;
    const deltaValue = (deltaX / containerWidth) * 10;
    const newValue = dragStartValue + deltaValue;

    applyThreshold(dragging, newValue);
  };

  // Clamp a threshold between its neighbours and update it (used by drag and keyboard)
  const applyThreshold = (threshold: string, newValue: number) => {
    switch (threshold) {
      case 'low':
        const lowMax = mediumAlertsEnabled ? Math.min(10, mediumThreshold - 0.1) : 10;
        setLowThreshold(Math.max(0, Math.min(newValue, lowMax)));
        break;
      case 'medium':
        const mediumMin = lowAlertsEnabled ? lowThreshold + 0.1 : 0;
        const mediumMax = highAlertsEnabled ? Math.min(10, highThreshold - 0.1) : 10;
        setMediumThreshold(Math.max(mediumMin, Math.min(newValue, mediumMax)));
        break;
      case 'high':
        const highMin = mediumAlertsEnabled ? mediumThreshold + 0.1 : lowAlertsEnabled ? lowThreshold + 0.1 : 0;
        const highMax = criticalAlertsEnabled ? Math.min(10, criticalThreshold - 0.1) : 10;
        setHighThreshold(Math.max(highMin, Math.min(newValue, highMax)));
        break;
    }
  };

  const handleDragEnd = () => {
    setDragging(null);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => handleDragMove(e.clientX);
    const handleMouseUp = () => handleDragEnd();
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches[0]) handleDragMove(e.touches[0].clientX);
    };
    const handleTouchEnd = () => handleDragEnd();

    if (dragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [dragging, dragStartX, dragStartValue, containerRef, lowAlertsEnabled, mediumAlertsEnabled, highAlertsEnabled, criticalAlertsEnabled, lowThreshold, mediumThreshold, highThreshold, criticalThreshold]);

  // Helper function to reset to default CVSS ranges
  const resetToDefaults = () => {
    setLowThreshold(3.9);
    setMediumThreshold(6.9);
    setHighThreshold(8.9);
    setCriticalThreshold(10.0);
    setLowAlertsEnabled(false);
    setMediumAlertsEnabled(true);
    setHighAlertsEnabled(true);
    setCriticalAlertsEnabled(true);
  };


  const handleCancelChanges = () => {
    if (changesMade) {
      setLowSwitch(initialLowSwitch);
      setMediumSwitch(initialMediumSwitch);
      setHighSwitch(initialHighSwitch);
      setCriticalSwitch(initialCriticalSwitch);
      setChangesMade(false);
    } else {
      setLowSwitch(initialLowSwitch);
      setMediumSwitch(initialMediumSwitch);
      setHighSwitch(initialHighSwitch);
      setCriticalSwitch(initialCriticalSwitch);
    }
    getEnvironmentSettings();
  };

  console.log('opIDDD', masterOperatorId);
  // Add this function to calculate coverage percentage
  const calculateCoverage = () => {
    const enabledRanges = [];

    if (lowAlertsEnabled) enabledRanges.push([0, lowThreshold]);
    if (mediumAlertsEnabled) enabledRanges.push([lowThreshold, mediumThreshold]);
    if (highAlertsEnabled) enabledRanges.push([mediumThreshold, highThreshold]);
    if (criticalAlertsEnabled) enabledRanges.push([highThreshold, criticalThreshold]);

    // Calculate total covered range
    const totalCovered = enabledRanges.reduce((sum, [start, end]) => sum + (end - start), 0);

    // Calculate percentage of total CVSS range (0-10)
    return ((totalCovered / 10) * 100).toFixed(1);
  };

  const handleApply = async () => {
    const stateData = {
      lowAlert: lowSwitch,
      mediumAlert: mediumSwitch,
      highAlert: highSwitch,
      criticalAlert: criticalSwitch,
      low: selectedLows,
      medium: selectedMediums,
      high: selectedHighs,
      critical: selectedCriticals,
    };

    try {
      // awaited so a failed save reaches the catch instead of reporting success
      const response = await axios.post(`/v2/op/master-operator/create-cvss-ratings/${masterOperatorId}`, {
        lowAlert: lowSwitch,
        mediumAlert: mediumSwitch,
        highAlert: highSwitch,
        criticalAlert: criticalSwitch,
        low: selectedLows,
        medium: selectedMediums,
        high: selectedHighs,
        critical: selectedCriticals,
      });
      console.log(response);
    } catch (error) {
      console.log(error);
      return false; // keep unsaved changes so the user can retry
    }
    // const serializedState = JSON.stringify(stateData);
    // localStorage.setItem('environmentSettings', serializedState);

    // setInitialLowSwitch(lowSwitch);
    // setInitialMediumSwitch(mediumSwitch);
    // setInitialHighSwitch(highSwitch);
    // setInitialCriticalSwitch(criticalSwitch);

    setChangesMade(false);
    return true;
  };

  async function getEnvironmentSettings() {
    try {
      const serializedState = await axios.get(`/v2/op/master-operator/list-cvss-ratings/${masterOperatorId}`);
      console.log('Ajay : ', operatorId);
      if (serializedState.data.cvssRatings) {
        const cvssRatingData = serializedState.data.cvssRatings;
        setSelectedLows(cvssRatingData.low);
        setSelectedMediums(cvssRatingData.medium);
        setSelectedHighs(cvssRatingData.high);
        setSelectedCriticals(cvssRatingData.critical);
        setLowSwitch(cvssRatingData.lowAlert);
        setMediumSwitch(cvssRatingData.mediumAlert);
        setHighSwitch(cvssRatingData.highAlert);
        setCriticalSwitch(cvssRatingData.criticalAlert);
      }
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    getEnvironmentSettings();
    setChangesMade(false);
  }, [masterOperatorId]);

  function handleMenuClick(menuName: string) {
    setSelectedMenu(menuName);
  }

  function getOperatorName() {
    return operatorList?.find((item: any) => item.key == operatorId)?.name;
  }

  const handleLevelMatch = (selectedLevelSet: any, levels: any) => {
    selectedLevelSet(levels);
    setSelectedLows(levels);
    setSelectedMediums(levels);
    setSelectedHighs(levels);
    setSelectedCriticals(levels);
  };

  const handleArrayItemClick = (
    index: number,
    selectedArray: number[],
    setSelectedArray: React.Dispatch<React.SetStateAction<number[]>>,
    otherArrays: { array: number[]; setter: React.Dispatch<React.SetStateAction<number[]>> }[]
  ) => {
    const selectedRange = selectedArray.includes(index)
      ? Array.from(Array(11).keys()).slice(index)
      : Array.from(Array(11).keys()).slice(index);

    if (selectedArray) {
      const lastArrayIndex = selectedArray[selectedArray.length - 1];
      if (lastArrayIndex < index) {
        const merge = selectedArray.concat(selectedRange);
        setSelectedArray(merge);
      } else if (lastArrayIndex > index) {
        const merge = selectedRange.concat(selectedArray);
        setSelectedArray(merge);
      } else {
        setSelectedArray(selectedRange);
      }
    }
    otherArrays.forEach(({ array, setter }) => {
      setter(array.filter((item) => index >= item + 1));
    });
    console.log(otherArrays);
  };

  interface LanOrdder {
    defaultLanguage: string;
    order: string[];
  }

  useEffect(() => {
    async function loadLanguageOrder() {
      const lan = await axios
        .get(`/v2/op/master-operator/fetch-account-language/${masterOperatorId}`)
        .then((response) => {
          setLanguageOrder({
            defaultLanguage: response?.data?.languageSettings?.defaultLanguage || '',
            order: response?.data?.languageSettings?.languageOrder || [],
          });
        })
        .catch((err) => {
          console.log(err);
        });
      console.log('lanorder', lan);
    }
    loadLanguageOrder();
  }, [masterOperatorId]);

  const [languageOrder, setLanguageOrder] = useState<LanOrdder>({ defaultLanguage: '', order: [] });

  const headerStyleLow =
    'bg-[#3BFF00] text-black font-bold font-md p-2 shadow-md rounded-lg w-1/4 h-20 flex items-center justify-center';
  const headerStyleMed =
    'bg-[#FFFC00] text-black font-bold font-md p-2 shadow-md rounded-lg w-1/4 h-20 flex items-center justify-center';
  const headerStyleHigh =
    'bg-[#FF0000] text-white font-bold font-md p-2 shadow-md rounded-lg w-1/4 h-20 flex items-center justify-center';
  const headerStyleCritical =
    'bg-[#101820] text-white font-bold font-md p-2 shadow-md rounded-lg w-1/4 h-20 flex items-center justify-center';

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    index: number,
    selectedArray: number[],
    setSelectedArray: React.Dispatch<React.SetStateAction<number[]>>,
    otherArrays: { array: number[]; setter: React.Dispatch<React.SetStateAction<number[]>> }[]
  ) => {
    if (event.key === 'Enter') {
      handleArrayItemClick(index, selectedArray, setSelectedArray, otherArrays);
    }
  };

  const sortOrder = (index: number, scenario: string) => {
    switch (scenario) {
      case 'up':
        [languageOrder.order[index - 1], languageOrder.order[index]] = [languageOrder.order[index], languageOrder.order[index - 1]];
        setLanguageOrder({ ...languageOrder, order: languageOrder.order });
        break;
      case 'down':
        [languageOrder.order[index], languageOrder.order[index + 1]] = [languageOrder.order[index + 1], languageOrder.order[index]];
        setLanguageOrder({ ...languageOrder, order: languageOrder.order });
        break;
      default:
        return;
    }
    const payload = {
      defaultLanguage: languageOrder.defaultLanguage,
      languageOrder: languageOrder.order,
    };
    axios
      .post(`/v2/op/master-operator/update-account-language/${masterOperatorId}`, payload)
      .then((res) => {
        console.log(res);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const defaultLanguage = (e: any) => {
    const payload = {
      defaultLanguage: e.target.value,
      languageOrder: languageOrder.order,
    };
    axios
      .post(`/v2/op/master-operator/update-account-language/${masterOperatorId}`, payload)
      .then((res) => {
        console.log(res);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const languageName = (code: string) =>
    code === 'en' ? 'English' : code === 'es' ? 'Spanish' : code === 'rs' ? 'Serbian' : code === 'se' ? 'Swedish' : code;

  // Translated display name for an account language code (raw code fallback)
  const languageLabel = (code: string) =>
    ['en', 'es', 'rs', 'se'].includes(code) ? t(`settings.language.names.${code}`) : code;

  const options =
    languageOrder?.order.length > 1 &&
    languageOrder?.order
      .filter((code) => code !== languageOrder?.defaultLanguage)
      .map((code) => (
        <option key={code} value={code}>
          {languageLabel(code)}
        </option>
      ));

  return (
    <div className="bd st-page" data-theme={theme} data-accent="mint">
      <section id="settings-details" className="st-wrap">
        <header className="bd-page-hd">
          <span className="bd-eyebrow">{t('settings.eyebrow')}</span>
          <h1 className="bd-h1 bd-page-title">{t('settings.title')}</h1>
          <span className="bd-body">
            {t('settings.subtitle')}
          </span>
        </header>
        <div className="st-body">
          {/* Tabs Navigation */}
          <div className="bd-tabbar" role="tablist" aria-label={t('settings.tabsAriaLabel')}>
            {menuList.map((item) => {
              const Icon = tabIcons[item];
              const active = selectedMenu === item;
              return (
                <button
                  key={item}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => handleMenuClick(item)}
                  className={`bd-tabbar-item ${active ? 'active' : ''}`}
                >
                  {Icon && <Icon size={16} strokeWidth={1.9} />}
                  <span>{tabLabels[item] ?? item}</span>
                </button>
              );
            })}
          </div>

          {/* Tab Panels */}
          <div className="bd-card st-panel">
            {/* Global Filters Tab */}
            {selectedMenu === 'Global Filters' && (
              <div className="container-settings">
                <WebFilters darkmode={darkmode} />
              </div>
            )}

            {/* CVSS Alerts Tab */}
            {selectedMenu === 'CVSS Alerts' && (
              <CvssThresholdConfig
                theme={theme}
                thresholds={{ low: lowThreshold, medium: mediumThreshold, high: highThreshold, critical: criticalThreshold }}
                setThreshold={{
                  low: setLowThreshold,
                  medium: setMediumThreshold,
                  high: setHighThreshold,
                  critical: setCriticalThreshold,
                }}
                enabled={{
                  low: lowAlertsEnabled,
                  medium: mediumAlertsEnabled,
                  high: highAlertsEnabled,
                  critical: criticalAlertsEnabled,
                }}
                setEnabled={{
                  low: setLowAlertsEnabled,
                  medium: setMediumAlertsEnabled,
                  high: setHighAlertsEnabled,
                  critical: setCriticalAlertsEnabled,
                }}
                dragging={dragging}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                applyThreshold={applyThreshold}
                containerRef={containerRef}
                setContainerRef={setContainerRef}
                valueFromPosition={calculateValueFromPosition}
                coverage={calculateCoverage()}
                onApply={async () => {
                  const saved = await handleApply();
                  showToastMessage(saved ? t('settings.toast.cvssUpdated') : t('settings.toast.cvssFailed'));
                }}
                onDiscard={handleCancelChanges}
                onReset={resetToDefaults}
              />
            )}

            {/* UI Settings Tab */}
            {selectedMenu === 'UI Settings' && (
              <div className="container-settings">
                <UISettings darkmode={darkmode}></UISettings>
              </div>
            )}

            {/* Select Language Tab */}
            {selectedMenu === 'Select Language' && (
              <div className="st-lang">
                <div className="st-section-hd">
                  <h2 className="bd-h2">{t('settings.language.title')}</h2>
                  <span className="bd-body">{t('settings.language.subtitle')}</span>
                </div>

                <div className="st-lang-grid">
                  <div className="bd-card st-lang-card">
                    <div className="st-lang-card-hd">
                      <h3 className="bd-h3">{t('settings.language.defaultTitle')}</h3>
                      <span className="bd-meta">{t('settings.language.defaultSubtitle')}</span>
                    </div>
                    <div className="bd-select">
                      <select aria-label={t('settings.language.defaultAriaLabel')} onChange={(e) => defaultLanguage(e)}>
                        {/* value keeps the previous (English-name) option value so the posted payload is unchanged */}
                        <option value={languageName(languageOrder.defaultLanguage)}>{languageLabel(languageOrder.defaultLanguage)}</option>
                        {options}
                      </select>
                      <ChevronDown size={14} />
                    </div>
                  </div>

                  <div className="bd-card st-lang-card">
                    <div className="st-lang-card-hd">
                      <h3 className="bd-h3">{t('settings.language.orderTitle')}</h3>
                      <span className="bd-meta">{t('settings.language.orderSubtitle')}</span>
                    </div>
                    <ol className="st-lang-list">
                      {languageOrder.order.map((order, index) => (
                        <li key={order} className="st-lang-item">
                          <span className="st-lang-rank bd-mono">{index + 1}</span>
                          <span className="st-lang-name">{languageLabel(order)}</span>
                          {order === languageOrder.defaultLanguage && <span className="bd-chip mint">{t('settings.language.defaultChip')}</span>}
                          <div className="st-lang-moves">
                            <button
                              type="button"
                              className="bd-iconbtn st-move"
                              aria-label={t('settings.language.moveUp', { language: languageLabel(order) })}
                              disabled={index === 0}
                              onClick={() => sortOrder(index, 'up')}
                            >
                              <ChevronUp size={15} />
                            </button>
                            <button
                              type="button"
                              className="bd-iconbtn st-move"
                              aria-label={t('settings.language.moveDown', { language: languageLabel(order) })}
                              disabled={index === languageOrder.order.length - 1}
                              onClick={() => sortOrder(index, 'down')}
                            >
                              <ChevronDown size={15} />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};